import { normalizarDocumento } from "./documento.js";

/**
 * Helpers para acessar campos da empresa tolerando diferentes formatos
 * do backend (camelCase, snake_case, variações semânticas).
 *
 * Replica o contrato usado no CadastroEmpresa para que o Acompanhamento
 * deste projeto consiga ler respostas de qualquer um dos dois backends.
 */

const CAMPOS_NOME = [
  // Campos derivados (backend novo - 2026-09: payload já vem com
  // empresa_nome e estudante_nome no top-level quando a lista vem
  // de /acompanhamentos/me ou /tce/estudante/<cpf>/autorizacoes).
  "empresa_nome",
  "estudante_nome",
  "razao_social",
  "razaoSocial",
  "razaosocial",
  "nome_fantasia",
  "nomeFantasia",
  "nome",
  "NOME",
  "RAZAO_SOCIAL",
];

const CAMPOS_DOCUMENTO = [
  // Campos derivados (backend novo - 2026-09).
  "estudante_cpf",
  "cnpj",
  "CNPJ",
  "cpf",
  "CPF",
  "documento",
  "documento_fiscal",
  "DOCUMENTO",
];

/**
 * Identificadores do **TCA Empregador** (registro do contrato de
 * aprendizagem na tabela `tca_empregador`). Devem ter prioridade sobre
 * o `id` da Empresa porque é o `TcaEmpregador.id` que a rota
 * `/tca-empregador/:id/status-completo` espera.
 *
 * Ordem de preferência:
 *   tca_id → tcaId → tca_empregador_id → tca_empregadorId → id (fallback)
 */
const CAMPOS_TCA_ID = [
  "tca_id",
  "tcaId",
  "tca_empregador_id",
  "tca_empregadorId",
  "idTcaEmpregador",
  "tce_id",
  "tceId",
  "id_tce",
  "id_tca",
  "id_tca_empregador",
  "autorizacao_id",
  "autorizacaoId",
];

/**
 * Identificadores da **Empresa** (tabela `empresa`). Usados quando o
 * objetivo é abrir detalhe da Empresa (ex: `/tca-empregador/empresa/:id`)
 * ou filtrar a lista por Empresa (ex: `?empresa_id=...`).
 *
 * `id` da Empresa só é confiável quando NÃO existe `tca_id` no payload.
 */
const CAMPOS_ID = [
  "id",
  "ID",
  "id_empresa",
  "empresa_id",
  "idEmpresa",
  "ID_EMPRESA",
];

const CHAVES_LISTA_RESPOSTA = [
  "data",
  "rows",
  "results",
  "empresas",
  "items",
  "itens",
];

/**
 * Resolve o id da Empresa presente no payload.
 *
 * ATENÇÃO: este helper **só lê** os CAMPOS_ID tradicionais. Quando o
 * payload traz um `tca_id` explícito (ou `tca_empregador_id`), o `id`
 * no top-level do JSON passa a ser interpretado como `TcaEmpregador.id`
 * (contrato do `TcaEmpregadorController.index()`), e este helper pode
 * devolver o id errado. Para esses casos use `obterIdTca()` ou
 * `obterIdEmpresaReal()` (abaixo).
 */
export function obterIdEmpresa(empresa) {
  if (!empresa || typeof empresa !== "object") return null;

  const idValido = CAMPOS_ID.map((campo) => empresa[campo]).find(
    (valor) => Number.isInteger(Number(valor)) && Number(valor) > 0,
  );

  return idValido ? Number(idValido) : null;
}

/**
 * Resolve o id do **TCA Empregador** presente no payload. Este é o id
 * que deve ser enviado para:
 *   - GET /tca-empregador/:id/status-completo
 *   - GET /tca-empregador/:id (detalhe legado)
 *
 * Ordem de preferência:
 *   1) campos explícitos do TCA (`tca_id`, `tcaId`, `tca_empregador_id`, …)
 *   2) `id` / `ID` (quando o payload está no formato "raiz do TCA")
 *   3) `empresa.id` apenas como último recurso (assume que o registro
 *      do TCA está embutido em `empresa`)
 *
 * @param {object|null|undefined} item
 * @returns {number|null}
 */
export function obterIdTca(item) {
  if (!item || typeof item !== "object") return null;

  // 1) Campos explícitos do TCA Empregador
  for (const campo of CAMPOS_TCA_ID) {
    const valor = item[campo];
    const numero = Number(valor);
    if (Number.isInteger(numero) && numero > 0) return numero;
  }

  // 2) `id` / `ID` no top-level (caso clássico: a lista devolve o
  //    registro do TCA Empregador na raiz)
  for (const campo of ["id", "ID"]) {
    const valor = item[campo];
    const numero = Number(valor);
    if (Number.isInteger(numero) && numero > 0) return numero;
  }

  // 3) `empresa.id` como último recurso (registro do TCA embutido em `empresa`)
  const empresa = item.empresa;
  if (empresa && typeof empresa === "object") {
    for (const campo of ["id", "ID"]) {
      const valor = empresa[campo];
      const numero = Number(valor);
      if (Number.isInteger(numero) && numero > 0) return numero;
    }
  }

  return null;
}

/**
 * Resolve o id **real da Empresa**, ignorando o `tca_id` (mesmo quando
 * ele está presente no top-level do payload).
 *
 * Use este helper quando a chamada de API é para abrir o detalhe da
 * Empresa (ex: `GET /empresa/:id`), ou quando o filtro da lista
 * precisa do `empresa_id` (não `tca_empregador_id`).
 *
 * @param {object|null|undefined} item
 * @returns {number|null}
 */
export function obterIdEmpresaReal(item) {
  if (!item || typeof item !== "object") return null;

  // 1) `empresa.id` tem prioridade absoluta (é o id da Empresa no JOIN).
  const empresa = item.empresa;
  if (empresa && typeof empresa === "object") {
    for (const campo of ["id", "ID"]) {
      const valor = empresa[campo];
      const numero = Number(valor);
      if (Number.isInteger(numero) && numero > 0) return numero;
    }
  }

  // 2) Campos tradicionais de Empresa
  const idValido = CAMPOS_ID.map((campo) => item[campo]).find(
    (valor) => Number.isInteger(Number(valor)) && Number(valor) > 0,
  );
  return idValido ? Number(idValido) : null;
}

export function obterNomeEmpresa(empresa) {
  if (!empresa || typeof empresa !== "object") return "";

  const encontrado = CAMPOS_NOME.map((campo) => empresa[campo]).find((valor) =>
    String(valor || "").trim(),
  );

  return String(encontrado || "").trim();
}

export function obterDocumentoEmpresa(empresa) {
  if (!empresa || typeof empresa !== "object") return "";

  const encontrado = CAMPOS_DOCUMENTO.map((campo) => empresa[campo]).find(
    (valor) => String(valor || "").trim(),
  );

  return String(encontrado || "").trim();
}

/** Resolve o "objeto empresa" dentro de diferentes formatos de payload. */
export function normalizarEmpresaResposta(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const chavesDiretas = ["data", "item", "empresa", "result", "cadastro"];
  for (const chave of chavesDiretas) {
    const candidato = payload[chave];
    if (candidato && typeof candidato === "object") return candidato;
  }
  return payload;
}

export function normalizarListaEmpresas(payload) {
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === "object") {
    for (const chave of CHAVES_LISTA_RESPOSTA) {
      if (Array.isArray(payload[chave])) return payload[chave];
    }
  }
  return [];
}

/**
 * Normaliza o payload paginado/composto de `/acompanhamentos/me`.
 * A partir de 2026-09-25 a rota passa a devolver:
 *   { total, page, limit, pages, totalPages, data: [...] }
 * Por compatibilidade, mantemos suporte ao formato array puro
 * e a `{ data: [...] }` simples.
 *
 * @param {object|Array<any>} payload
 * @returns {{ lista: Array<any>, total: number, page: number, totalPages: number }}
 */
export function normalizarPayloadAcompanhamentosMe(payload) {
  const vazio = { lista: [], total: 0, page: 1, totalPages: 0 };
  if (!payload || typeof payload !== "object") return vazio;

  if (Array.isArray(payload)) {
    return {
      lista: payload,
      total: payload.length,
      page: 1,
      totalPages: 1,
    };
  }

  // Tenta extrair a lista com o mesmo conjunto de chaves do
  // `normalizarListaEmpresas`. Isso faz o helper funcionar tanto
  // para a nova rota agregada quanto para a rota legada.
  let lista = null;
  for (const chave of CHAVES_LISTA_RESPOSTA) {
    if (Array.isArray(payload[chave])) {
      lista = payload[chave];
      break;
    }
  }
  if (!lista) {
    if (Array.isArray(payload.data)) lista = payload.data;
  }
  if (!lista) return vazio;

  const total = Number(payload.total) || lista.length;
  const page = Number(payload.page) || 1;
  const totalPages =
    Number(payload.totalPages) ||
    Number(payload.pages) ||
    Math.max(1, Math.ceil(total / Math.max(1, Number(payload.limit) || 50)));

  return { lista, total, page, totalPages };
}

export function empresaCorrespondeDocumento(empresa, documentoDigitado) {
  if (!empresa || typeof empresa !== "object") return false;

  const docLimpo = normalizarDocumento(documentoDigitado);
  if (!docLimpo) return false;

  return CAMPOS_DOCUMENTO.some((campo) => {
    const valor = empresa[campo];
    if (typeof valor !== "string") return false;
    return valor.replace(/\D/g, "") === docLimpo;
  });
}

export function encontrarEmpresaCorreta(dados, documentoDigitado) {
  if (Array.isArray(dados)) {
    return (
      dados.find((item) => empresaCorrespondeDocumento(item, documentoDigitado)) ||
      null
    );
  }

  if (dados && typeof dados === "object") {
    if (empresaCorrespondeDocumento(dados, documentoDigitado)) return dados;

    for (const chave of CHAVES_LISTA_RESPOSTA) {
      if (Array.isArray(dados[chave])) {
        const encontrada = dados[chave].find((item) =>
          empresaCorrespondeDocumento(item, documentoDigitado),
        );
        if (encontrada) return encontrada;
      }
    }
  }

  return null;
}

export function montarDescricaoEmpresa(empresaRef) {
  if (!empresaRef) return "Empresa não identificada";

  const nome = String(empresaRef.nome || "").trim();
  const documento = String(empresaRef.documento || "").trim();

  if (nome && documento) return `${nome} - ${documento}`;
  if (nome) return nome;
  if (documento) return documento;
  if (empresaRef.id) return `Empresa #${empresaRef.id}`;
  return "Empresa não identificada";
}
